import {
  createContext,
  useContext,
  useCallback,
  useRef,
  useMemo,
  useEffect,
  type ReactNode,
} from "react";
import { useUnit } from "effector-react";
import { useViewportSize } from "./ViewportContext";
import { $appConfig, updateCameraConfig } from "../state/appConfig";
import { $safeAreaInsets, type SafeAreaInsets, ZERO_SAFE_AREA } from "../state/safeArea";

export type ChatPanelMode =
  | "hidden"
  // | "third-screen"
  | "half-screen"
  | "full-screen";

// Layout constants
export const HUD_INDENT = 12;
export const CHAT_INPUT_HEIGHT = 56;
export const PANEL_SUGGESTIONS_GAP = 16;
export const SUGGESTIONS_HEIGHT = 36;

/**
 * Default camera view offset for "hidden" chat panel mode.
 * This is a fixed value that doesn't depend on viewport size.
 * Used to initialize camera offset before React components mount.
 */
export const DEFAULT_HIDDEN_MODE_OFFSET =
  CHAT_INPUT_HEIGHT +
  2 * HUD_INDENT +
  PANEL_SUGGESTIONS_GAP +
  SUGGESTIONS_HEIGHT; // = 130

export const getCurtainHeight = (
  mode: ChatPanelMode,
  viewportHeight: number,
  shift = 0,
  safeArea: SafeAreaInsets = ZERO_SAFE_AREA
): number => {
  const baseHeight = (() => {
    switch (mode) {
      // case "third-screen":
      //   return 0.33 * viewportHeight - 2 * PANEL_INDENT;
      case "half-screen":
        return 0.5 * viewportHeight;
      case "full-screen":
        return Math.max(0, viewportHeight - safeArea.top);
      case "hidden":
      default:
        return (
          safeArea.bottom +
          CHAT_INPUT_HEIGHT +
          2 * HUD_INDENT +
          PANEL_SUGGESTIONS_GAP +
          SUGGESTIONS_HEIGHT
        );
    }
  })();

  return Math.max(0, baseHeight - shift);
};

export const getCurtainOffset = (
  mode: ChatPanelMode,
  viewportHeight: number,
  shift = 0,
  safeArea: SafeAreaInsets = ZERO_SAFE_AREA
) => getCurtainHeight(mode, viewportHeight, shift, safeArea);

interface CameraViewOffsetContextValue {
  /** Apply camera view offset with optional animation */
  applyCurtainOffset: (
    bottomOffset: number,
    animate: boolean,
    durationMs?: number
  ) => void;
  /** Calculate curtain offset for given mode and shift */
  getCurtainOffset: (mode: ChatPanelMode, shift?: number) => number;
  /** Current viewport dimensions */
  viewport: { width: number; height: number };
  /** Whether initial offset has been applied */
  isInitialized: boolean;
}

const CameraViewOffsetContext = createContext<CameraViewOffsetContextValue | null>(null);

interface CameraViewOffsetProviderProps {
  children: ReactNode;
  /** 
   * When true, applies default offset immediately (without animation).
   * Typically set to true when scene preview is ready.
   */
  enabled?: boolean;
}

export function CameraViewOffsetProvider({
  children,
  enabled = false,
}: CameraViewOffsetProviderProps) {
  const viewport = useViewportSize();
  const safeArea = useUnit($safeAreaInsets);
  const { appConfig, pushCameraConfig } = useUnit({
    appConfig: $appConfig,
    pushCameraConfig: updateCameraConfig,
  });
  
  // Initialize from current store state to sync with createScene.ts
  const initialOffset = appConfig.camera.viewOffset.enabled
    ? appConfig.camera.viewOffset.bottom
    : null;
  const bottomOffsetRef = useRef<number | null>(initialOffset);
  const hasAppliedInitialRef = useRef(false);

  const applyCurtainOffset = useCallback(
    (bottomOffset: number, animate: boolean, durationMs?: number) => {
      const roundedOffset = Math.round(bottomOffset);
      const maxOffset = Math.round(0.5 * viewport.height);
      const clampedOffset = Math.min(
        roundedOffset,
        maxOffset
      );

      // Skip if same offset and not animating (prevents duplicate calls)
      if (!animate && bottomOffsetRef.current === clampedOffset) {
        return;
      }

      bottomOffsetRef.current = clampedOffset;

      pushCameraConfig({
        viewOffset: {
          enabled: true,
          top: safeArea.top,
          right: 0,
          left: 0,
          bottom: clampedOffset,
          animate,
          animationDurationMs: durationMs,
        },
      });
    },
    [pushCameraConfig, safeArea.bottom, safeArea.top, viewport.height]
  );

  const getCurtainOffsetForMode = useCallback(
    (mode: ChatPanelMode, shift = 0) =>
      getCurtainOffset(mode, viewport.height, shift, safeArea),
    [safeArea, viewport.height]
  );

  // Apply default offset when enabled becomes true (e.g., at preview stage)
  useEffect(() => {
    if (enabled && !hasAppliedInitialRef.current) {
      hasAppliedInitialRef.current = true;
      applyCurtainOffset(
        getCurtainOffset("hidden", viewport.height, 0, safeArea),
        false
      );
    }
  }, [enabled, applyCurtainOffset, safeArea, viewport.height]);

  // Re-apply offset when viewport changes (if already initialized)
  useEffect(() => {
    if (hasAppliedInitialRef.current && bottomOffsetRef.current !== null) {
      // Recalculate with current offset value to handle viewport resize
      const currentOffset = bottomOffsetRef.current;
      const clampedOffset = Math.min(
        currentOffset,
        Math.round(0.5 * viewport.height)
      );
      if (clampedOffset !== bottomOffsetRef.current) {
        bottomOffsetRef.current = clampedOffset;
        pushCameraConfig({
          viewOffset: {
            enabled: true,
            top: safeArea.top,
            right: 0,
            left: 0,
            bottom: clampedOffset,
            animate: false,
          },
        });
      }
    }
  }, [viewport.height, safeArea.bottom, safeArea.top, pushCameraConfig]);

  const value = useMemo<CameraViewOffsetContextValue>(
    () => ({
      applyCurtainOffset,
      getCurtainOffset: getCurtainOffsetForMode,
      viewport,
      isInitialized: hasAppliedInitialRef.current,
    }),
    [applyCurtainOffset, getCurtainOffsetForMode, viewport]
  );

  return (
    <CameraViewOffsetContext.Provider value={value}>
      {children}
    </CameraViewOffsetContext.Provider>
  );
}

export function useCameraViewOffset(): CameraViewOffsetContextValue {
  const context = useContext(CameraViewOffsetContext);
  if (!context) {
    throw new Error(
      "useCameraViewOffset must be used within CameraViewOffsetProvider"
    );
  }
  return context;
}
